import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, FileText, Receipt } from "lucide-react";
import api from "@/utils/api";
import useDashboardFilters from "@/hooks/useDashboardFilters";
import DashboardFilters from "@/components/dashboard/DashboardFilters";
import KPIGrid from "@/components/dashboard/KPIGrid";
import TimeEntryDistributionChart from "@/components/dashboard/charts/TimeEntryDistributionChart";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const fmtHours = (v) =>
  `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v || 0)}h`;

const fmtCount = (v) =>
  new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(v || 0);

export default function DashboardTimeEntries() {
  const navigate = useNavigate();
  const { debouncedFilters, isHydrated } = useDashboardFilters();

  const canFetch = Boolean(isHydrated && debouncedFilters.dateFrom && debouncedFilters.dateTo);

  const params = useMemo(() => {
    const p = {
      date_from: debouncedFilters.dateFrom,
      date_to: debouncedFilters.dateTo,
    };
    if (debouncedFilters.projectIds?.length) p.project_ids = debouncedFilters.projectIds;
    if (debouncedFilters.customerIds?.length) p.customer_ids = debouncedFilters.customerIds;
    return p;
  }, [debouncedFilters]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", "time-entries-stats", params],
    queryFn: () => api.get("/dashboard/time-entries-stats", { params }).then((r) => r.data),
    enabled: canFetch,
    staleTime: 30_000,
  });

  const kpiItems = useMemo(() => {
    if (!data) return [];
    return [
      {
        key: "total-hours",
        title: "Gesamtstunden",
        value: data.total_hours,
        valueFormatter: fmtHours,
        testId: "kpi-total-hours",
      },
      {
        key: "open-hours",
        title: "Offene Stunden",
        value: data.open_hours,
        valueFormatter: fmtHours,
        metaLabel: "Einträge",
        metaValue: `${data.open_entries?.length || 0} offen`,
        testId: "kpi-open-hours",
      },
      {
        key: "invoiced-hours",
        title: "Abgerechnete Stunden",
        value: data.invoiced_hours,
        valueFormatter: fmtHours,
        testId: "kpi-invoiced-hours",
      },
      {
        key: "avg-per-day",
        title: "Ø Stunden / Tag",
        value: data.avg_hours_per_day,
        valueFormatter: fmtHours,
        metaLabel: "Arbeitstage",
        metaValue: fmtCount(data.days_with_entries),
        testId: "kpi-avg-hours-per-day",
      },
    ];
  }, [data]);

  return (
    <div className="space-y-6" data-testid="dashboard-time-entries-view">
      <DashboardFilters
        title="Zeiteinträge-Filter"
        subtitle="Auswertung nach Projekten & Zeitraum"
        showStatus={false}
        showProjects
        showCustomers
      />

      <KPIGrid items={kpiItems} isLoading={isLoading} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TimeEntryDistributionChart
          data={data?.by_project || []}
          isLoading={isLoading}
          error={error}
        />

        {/* Projects with quick-invoice buttons */}
        <Card data-testid="project-hours-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Receipt className="h-4 w-4 text-slate-500" />
              Offene Stunden pro Projekt
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 rounded-lg bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : !data?.by_project?.length ? (
              <div className="flex flex-col items-center justify-center py-12 text-sm text-slate-400">
                <Clock className="h-8 w-8 mb-2 opacity-40" />
                Keine Zeiteinträge im gewählten Zeitraum
              </div>
            ) : (
              <div className="space-y-2">
                {data.by_project.map((p) => {
                  const openH = (p.open_minutes / 60).toFixed(1);
                  const totalH = (p.total_minutes / 60).toFixed(1);
                  const hasOpen = p.open_minutes > 0;
                  return (
                    <div
                      key={p.project_id}
                      className="flex items-center justify-between rounded-lg border border-slate-100 px-4 py-3 hover:bg-slate-50 transition-colors"
                      data-testid={`project-row-${p.project_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate text-sm">{p.project_name}</div>
                        <div className="text-xs text-slate-500">
                          {totalH}h gesamt · {openH}h offen · {p.entry_count} Einträge
                        </div>
                      </div>
                      {hasOpen && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-3 shrink-0 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300"
                          onClick={() => navigate(`/invoices?auto_project=${p.project_id}`)}
                          data-testid={`quick-invoice-${p.project_id}`}
                        >
                          <Receipt className="h-3.5 w-3.5 mr-1.5" />
                          Abrechnen
                        </Button>
                      )}
                      {!hasOpen && (
                        <Badge variant="secondary" className="ml-3 shrink-0 text-xs bg-emerald-50 text-emerald-700">
                          Abgerechnet
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Open time entries table - full width */}
      <Card data-testid="open-time-entries-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" />
            Offene Zeiteinträge
            {data?.open_entries?.length > 0 && (
              <Badge variant="secondary" className="text-xs">{data.open_entries.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-10 rounded-lg bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : !data?.open_entries?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-sm text-slate-400">
              <FileText className="h-8 w-8 mb-2 opacity-40" />
              Alle Stunden sind abgerechnet
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.open_entries.map((entry) => {
                const hours = (entry.duration_minutes / 60).toFixed(1);
                const dateStr = entry.start_time
                  ? new Date(entry.start_time).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" })
                  : "–";
                return (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2.5 text-sm hover:bg-slate-50 transition-colors"
                    data-testid={`open-entry-${entry.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">
                        {entry.description || "Ohne Beschreibung"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {entry.project_name} · {dateStr}
                      </div>
                    </div>
                    <Badge variant="secondary" className="ml-3 shrink-0 font-mono text-xs">
                      {hours}h
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
